import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import DocumentSidebar from './components/DocumentSidebar';
import WelcomeScreen from './components/WelcomeScreen';
import ChatWindow from './components/ChatWindow';
import fetchMethodRequest from '../../config/service';
import showToasterMessage from '../UI/ToasterMessage/toasterMessage';
import { Layout, MessageSquare, Info } from 'lucide-react';

const CasePredictAI = ({ t }) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [chatId, setChatId] = useState(null);
  const [chats, setChats] = useState([]);
  const location = useLocation();

  useEffect(() => {
    fetchChats();
  }, []);

  useEffect(() => {
    if (location.state?.selectChatId) {
      const stateChatId = location.state.selectChatId;
      const loadStateChat = async () => {
        setChatId(stateChatId);
        setIsLoading(true);
        setShowWelcome(false);
        try {
          const response = await fetchMethodRequest('GET', `case-prediction/history/${stateChatId}`);
          if (response && response.success) {
            setMessages(response.history);
          }
        } catch (error) {
          console.error("Error loading redirected chat:", error);
        } finally {
          setIsLoading(false);
        }
      };
      loadStateChat();
    }
  }, [location.state?.selectChatId]);

  const fetchChats = async () => {
    try {
      const response = await fetchMethodRequest('GET', 'case-prediction');
      if (response && response.success) {
        setChats(response.chats || []);
      }
    } catch (error) {
      console.error("Error fetching chats:", error);
    }
  };

  const handleSelectChat = async (chat) => {
    setChatId(chat._id);
    setIsLoading(true);
    setShowWelcome(false);
    try {
      const response = await fetchMethodRequest('GET', `case-prediction/history/${chat._id}`);
      if (response && response.success) {
        setMessages(response.history);
      }
    } catch (error) {
      console.error("Error fetching chat history:", error);
      showToasterMessage("Failed to load chat history", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (text) => {
    setShowWelcome(false);
    const newUserMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, newUserMessage]);

    setIsLoading(true);
    try {
      const payload = {
        message: text,
        chatId: chatId || "new",
        history: messages.map(m => ({ role: m.role, content: m.content })),
        tool_name: "case_predict_ai"
      };

      const response = await fetchMethodRequest('POST', 'case-prediction/interact', payload);

      if (response && response.success) {
        if (response.chatId) setChatId(response.chatId);
        const assistantMessage = { role: 'assistant', content: response.response };
        setMessages(prev => [...prev, assistantMessage]);
        if (!chatId) fetchChats();
      } else {
        showToasterMessage(response.message || "Failed to get analysis", 'error');
      }
    } catch (error) {
      showToasterMessage("An error occurred during analysis", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async (uploadSelection) => {
    const files = Array.isArray(uploadSelection)
      ? uploadSelection.filter(Boolean)
      : [uploadSelection].filter(Boolean);
    if (!files.length) return;

    // Reset previous chat state and show loader
    setChatId(null);
    setMessages([]);
    setIsLoading(true);
    setShowWelcome(false);
    try {
      const isUniqueDiscovery = files.length > 1;
      const endpoint = isUniqueDiscovery
        ? 'case-prediction/unique-case'
        : 'case-prediction/interact';
      const extraBody = isUniqueDiscovery
        ? {
          message: "Find the unique case from these uploaded documents and explain whether it is unique because of success, failure, risk, facts, strategy, or legal category.",
          chatId: chatId || "new",
          tool_name: "case_unique_discovery"
        }
        : {
          message: "Analyze this uploaded case and provide win probability, likely outcome, strategic guidance, risk assessment, and relevant precedent patterns.",
          chatId: chatId || "new",
          tool_name: "case_predict_ai",
          history: JSON.stringify(messages)
        };

      const response = await fetchMethodRequest(
        'POST', endpoint, files, 'upload', true,
        { ...extraBody, chatId: 'new' }
      );

      if (response && response.success) {
        setMessages([{ role: 'assistant', content: response.response }]);
        setChatId(response.chatId);
        fetchChats();
        showToasterMessage(isUniqueDiscovery ? "Unique case discovery complete" : "Analysis complete", 'success');
      } else {
        showToasterMessage(response.message || "Failed to analyze file(s)", 'error');
      }
    } catch (error) {
      showToasterMessage("Upload failed", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenameChat = async (chatIdToRename, newTitle) => {
    try {
      const response = await fetchMethodRequest('PUT', `chats/${chatIdToRename}`, { question: newTitle });
      if (response && (response.success || response.respMessage || response.respCode === 200)) {
        showToasterMessage(response.respMessage || "Analysis renamed successfully", 'success');
        fetchChats();
      } else {
        showToasterMessage(response?.respMessage || "Failed to rename analysis", 'error');
      }
    } catch (error) {
      console.error("Error renaming chat:", error);
      showToasterMessage("Failed to rename analysis", 'error');
    }
  };

  const handleDeleteChat = async (chatIdToDelete) => {
    try {
      const response = await fetchMethodRequest('DELETE', `chats/${chatIdToDelete}`, {});
      if (response && (response.success || response.respMessage || response.respCode === 200 || response.respCode === 206)) {
        showToasterMessage(response.respMessage || "Analysis deleted successfully", 'success');
        fetchChats();
        if (chatId === chatIdToDelete) {
          setChatId(null);
          setMessages([]);
          setShowWelcome(true);
        }
      } else {
        showToasterMessage(response?.respMessage || "Failed to delete analysis", 'error');
      }
    } catch (error) {
      console.error("Error deleting chat:", error);
      showToasterMessage("Failed to delete analysis", 'error');
    }
  };

  return (
    <div className="workspace-container">
      <div className="workspace-layout">
        <DocumentSidebar
          onUpload={handleUpload}
          chats={chats}
          onSelectChat={handleSelectChat}
          selectedChatId={chatId}
          onRenameChat={handleRenameChat}
          onDeleteChat={handleDeleteChat}
        />

        <div className="main-viewport">
          <header className="viewport-header">
            <div className="header-left">
              <div className="view-badge">
                <MessageSquare size={12} />
                <span>CASE ANALYSIS</span>
              </div>
              <h1 className="view-title">CasePredictAI</h1>
            </div>
            <div className="header-right">
              <div className="info-chip">
                <Info size={13} />
                <span>AI-Native Legal Intelligence</span>
              </div>
            </div>
          </header>

          <main className="content-scroller">
            {showWelcome && messages.length === 0 ? (
              <WelcomeScreen />
            ) : (
              <ChatWindow
                messages={messages}
                onSendMessage={handleSendMessage}
                isLoading={isLoading}
              />
            )}
          </main>
        </div>
      </div>

      <style jsx>{`
        .workspace-container {
          height: calc(100vh - 64px);
          overflow: hidden;
          background: #ffffff;
          font-family: 'Inter', -apple-system, sans-serif;
        }

        .workspace-layout {
          display: flex;
          height: 100%;
        }

        .main-viewport {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
          background: #ffffff;
        }

        .viewport-header {
          height: 52px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 24px;
          border-bottom: 1px solid #f1f5f9;
          background: #ffffff;
          z-index: 20;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .view-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 2px 8px;
          background: #f1f5f9;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 700;
          color: #64748b;
          letter-spacing: 0.02em;
        }

        .view-title {
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .header-right {
          display: flex;
          align-items: center;
        }

        .info-chip {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: #94a3b8;
          font-weight: 500;
        }

        .content-scroller {
          flex: 1;
          overflow-y: auto;
          background: #f0f0f0;
        }

        /* Custom Scrollbar */
        .content-scroller::-webkit-scrollbar {
          width: 6px;
        }
        .content-scroller::-webkit-scrollbar-track {
          background: transparent;
        }
        .content-scroller::-webkit-scrollbar-thumb {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .content-scroller::-webkit-scrollbar-thumb:hover {
          background: #e2e8f0;
        }

        @media (max-width: 768px) {
          .viewport-header {
            padding: 0 16px;
          }
          .info-chip span {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default withTranslation('common')(CasePredictAI);
